// src/components/mensagens/MessagePreviewModal.jsx
// ============================================================================
// 🎯 COMPONENTE: MessagePreviewModal — V2 ENTERPRISE PORTAL
// 📋 Função: Modal de preview e edição de mensagem antes do envio
// 📱 Responsivo: Adaptado para mobile e desktop
// 🔥 V2: Portal a document.body · Dark mode automático
// ============================================================================

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Send,
  Eye,
  Edit2,
  AlertCircle,
  CheckCircle,
  Phone,
  User,
  Copy,
  Check,
  MessageSquare,
  Smartphone,
  Clock,
  Calendar
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';
import { RichMessageEditor } from '../RichMessageEditor';
import { formatPhoneDisplay, isValidPhone } from './helpers';

/**
 * 🎯 Componente de modal de preview de mensagem
 */
export const MessagePreviewModal = ({
  isOpen,
  client,
  template,
  message: initialMessage,
  onConfirm,
  onCancel,
  primaryColor = '#10b981'
}) => {
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const mounted = usePortal();

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onCancel);

  const [editedMessage, setEditedMessage] = useState(initialMessage);
  const [mode, setMode] = useState('preview');
  const [copied, setCopied] = useState(false);
  const [charCount, setCharCount] = useState(initialMessage?.length || 0);
  const [validation, setValidation] = useState({ isValid: true, message: '' });

  useEffect(() => {
    setEditedMessage(initialMessage);
    setCharCount(initialMessage?.length || 0);
  }, [initialMessage]);

  useEffect(() => {
    if (!editedMessage) {
      setValidation({ isValid: false, message: 'Mensagem não pode estar vazia' });
    } else if (editedMessage.length > 2000) {
      setValidation({ isValid: false, message: 'Mensagem muito longa (máx. 2000 caracteres)' });
    } else {
      setValidation({ isValid: true, message: '' });
    }
  }, [editedMessage]);

  const isPhoneValid = client?.phone ? isValidPhone(client.phone) : false;

  const handleCopy = () => {
    navigator.clipboard.writeText(editedMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = () => {
    if (!validation.isValid) return;
    if (!isPhoneValid) {
      alert('Telefone do cliente inválido');
      return;
    }
    onConfirm(editedMessage);
  };

  if (!isOpen) return null;

  // 🌓 Clases dark mode
  const bgModal = "bg-white dark:bg-slate-900";
  const borderLight = "border-slate-200 dark:border-slate-700";
  const textDark = "text-slate-900 dark:text-slate-100";
  const textGray = "text-slate-500 dark:text-slate-400";
  const textGrayStrong = "text-slate-600 dark:text-slate-300";
  const bgValid = "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800";
  const bgInvalid = "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800";
  const bgPreviewHeader = "bg-green-50 dark:bg-green-950/30";
  const bgPreviewBody = "bg-white dark:bg-slate-900";
  const bgBadge = "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300";
  const btnCancel = "border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800";
  const bgQuickPreview = "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700";
  const bgQuickHeader = "bg-slate-50 dark:bg-slate-800";
  const textRed = "text-red-500 dark:text-red-400";
  const bgRedLight = "bg-red-50 dark:bg-red-950/30";

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-3"
        onClick={onCancel}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className={`
            ${bgModal} rounded-2xl w-full mx-auto shadow-2xl border ${borderLight}
            ${isMobile ? 'max-w-full p-4' : 'max-w-3xl p-6'}
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <div className={`
                rounded-lg flex items-center justify-center
                ${mode === 'edit' ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-green-100 dark:bg-green-900/40'}
                ${isMobile ? 'p-1.5' : 'p-2'}
              `}>
                {mode === 'edit' ? (
                  <Edit2 size={isMobile ? 16 : 20} className="text-blue-600 dark:text-blue-400" />
                ) : (
                  <Eye size={isMobile ? 16 : 20} className="text-green-600 dark:text-green-400" />
                )}
              </div>
              <div>
                <h2 className={`font-bold ${textDark} ${isMobile ? 'text-base' : 'text-lg'}`}>
                  {mode === 'edit' ? 'Editar mensagem' : 'Confirmar envio'}
                </h2>
                <p className={`text-xs ${textGray}`}>
                  {mode === 'edit' 
                    ? 'Use **negrito**, _itálico_, [links](url) e listas'
                    : 'Revise a mensagem antes de enviar'}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              aria-label="Fechar"
            >
              <X size={isMobile ? 18 : 20} className={textGray} />
            </button>
          </div>

          {/* Informações do Cliente */}
          <div className={`mb-4 p-3 rounded-xl border ${isPhoneValid ? bgValid : bgInvalid}`}>
            <div className="flex items-center gap-2">
              <div className={`
                rounded-full flex items-center justify-center text-white font-medium
                bg-gradient-to-br from-green-500 to-emerald-600
                ${isMobile ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'}
              `}>
                {client?.name ? client.name[0].toUpperCase() : '?'}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold ${textDark} truncate ${isMobile ? 'text-sm' : ''}`}>
                  {client?.name || 'Cliente sem nome'}
                </h3>
                <div className={`flex items-center gap-2 text-xs ${textGray}`}>
                  {client?.phone ? (
                    <>
                      <Phone size={12} className="flex-shrink-0" />
                      <span className="truncate">{formatPhoneDisplay(client.phone)}</span>
                      {!isPhoneValid && (
                        <span className={`${textRed} text-[10px]`}>(inválido)</span>
                      )}
                    </>
                  ) : (
                    <span className={textRed}>Telefone não cadastrado</span>
                  )}
                </div>
              </div>
              {template && template !== 'padrao' && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${bgBadge} ${isMobile ? 'text-[10px]' : ''}`}>
                  {template}
                </span>
              )}
            </div>
          </div>

          {/* Área de edição/preview */}
          <div className="mb-4">
            {mode === 'edit' ? (
              <RichMessageEditor
                value={editedMessage}
                onChange={setEditedMessage}
                placeholder="Digite sua mensagem..."
                height={isMobile ? '180px' : '250px'}
              />
            ) : (
              <div className={`border rounded-xl overflow-hidden ${isPhoneValid ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}`}>
                <div className={`flex items-center justify-between px-3 py-2 border-b ${borderLight} ${isPhoneValid ? bgPreviewHeader : bgRedLight}`}>
                  <div className="flex items-center gap-2">
                    <MessageSquare size={14} className={isPhoneValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} />
                    <span className={`text-xs font-medium ${textGrayStrong}`}>Preview</span>
                  </div>
                  <button
                    onClick={() => setMode('edit')}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                  >
                    <Edit2 size={12} />
                    Editar
                  </button>
                </div>
                <div className={`prose prose-sm max-w-none p-4 ${bgPreviewBody} ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {editedMessage ? (
                    <div className={`whitespace-pre-wrap ${textDark}`}>{editedMessage}</div>
                  ) : (
                    <p className={`italic ${textGray}`}>Mensagem vazia</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Barra de informações */}
          <div className={`flex items-center justify-between mb-4 text-xs ${textGray}`}>
            <div className="flex items-center gap-2">
              <span>{charCount} caracteres</span>
              {!validation.isValid && (
                <span className={`${textRed} flex items-center gap-1`}>
                  <AlertCircle size={12} />
                  {validation.message}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1 hover:${textGrayStrong} transition-colors`}
              >
                {copied ? (
                  <>
                    <Check size={12} className="text-green-500" />
                    <span className="text-green-500">Copiado</span>
                  </>
                ) : (
                  <>
                    <Copy size={12} />
                    <span>Copiar</span>
                  </>
                )}
              </button>
              <span className="text-slate-300 dark:text-slate-600">•</span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {new Date().toLocaleTimeString('pt-BR')}
              </span>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <button
              onClick={onCancel}
              className={`px-4 py-3 border rounded-xl ${btnCancel} transition-colors font-medium ${isMobile ? 'text-sm py-2.5' : 'text-sm py-2'}`}
            >
              Cancelar
            </button>
            <button
              onClick={handleSend}
              disabled={!validation.isValid || !isPhoneValid}
              className={`px-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isMobile ? 'text-sm py-2.5' : 'text-sm py-2'}`}
              style={{ backgroundColor: primaryColor, color: 'white' }}
            >
              <Send size={isMobile ? 16 : 18} />
              Enviar mensagem
            </button>
          </div>

          {/* Avisos de segurança */}
          {!isPhoneValid && (
            <div className={`mt-3 p-2 ${bgRedLight} rounded-lg text-xs ${textRed} flex items-center gap-2`}>
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>Telefone inválido. Verifique o número do cliente antes de enviar.</span>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
};

/**
 * 🎯 Componente de preview rápido (versão simplificada)
 */
export const QuickPreview = ({ message, recipient, primaryColor = '#10b981' }) => {
  const isMobile = useIsMobile();
  const { isDark } = useTheme();

  const bgCard = "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700";
  const bgHeader = "bg-slate-50 dark:bg-slate-800";
  const textGray = "text-slate-500 dark:text-slate-400";
  const textGrayStrong = "text-slate-600 dark:text-slate-300";
  const bgBubble = "bg-green-100 dark:bg-green-900/30";
  const textBubble = "text-green-700 dark:text-green-300";

  return (
    <div className={`${bgCard} rounded-xl border overflow-hidden`}>
      <div className={`flex items-center gap-2 px-3 py-2 ${bgHeader} border-b border-slate-200 dark:border-slate-700`}>
        <Smartphone size={14} className={textGray} />
        <span className={`text-xs font-medium ${textGrayStrong}`}>Preview no WhatsApp</span>
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs">
            {recipient?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1">
            <div className={`${bgBubble} rounded-lg p-2 max-w-[80%]`}>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} whitespace-pre-wrap text-slate-800 dark:text-slate-100`}>
                {message || '...'}
              </p>
              <div className="text-right mt-1">
                <span className={`text-[8px] ${textBubble}`}>
                  {new Date().toLocaleTimeString('pt-BR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 🎯 Componente de alerta de confirmação
 */
export const ConfirmAlert = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirmar',
  message = 'Tem certeza que deseja continuar?'
}) => {
  const { isDark } = useTheme();
  const mounted = usePortal();

  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onCancel);

  if (!isOpen) return null;

  const bgModal = "bg-white dark:bg-slate-900";
  const textDark = "text-slate-900 dark:text-slate-100";
  const textGray = "text-slate-600 dark:text-slate-400";
  const btnNo = "border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800";

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 dark:bg-black/70 flex items-center justify-center z-[99999] p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className={`${bgModal} rounded-xl max-w-sm w-full p-4 shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={`font-semibold ${textDark} mb-2`}>{title}</h3>
        <p className={`text-sm ${textGray} mb-4`}>{message}</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className={`px-3 py-1.5 text-sm rounded-lg ${btnNo} transition-colors`}
          >
            Não
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Sim
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  return portalize(mounted, modalContent);
};

// ============================================================================
// 🎯 EXPORTAÇÃO PADRÃO
// ============================================================================
export default MessagePreviewModal;